import { useState } from 'react';

import { EllipsisVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { SavedFilterForm } from './SavedFilterForm';

interface RenameDeletePopoverProps {
  currentName?: string;
  onRename: (name: string) => string | null;
  onDeleteClick: () => void;
}

export const RenameDeletePopover = ({
  currentName,
  onRename,
  onDeleteClick,
}: RenameDeletePopoverProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showRenameForm, setShowRenameForm] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setShowRenameForm(false);
    }
  };

  const handleClose = () => {
    setShowRenameForm(false);
    setOpen(false);
  };

  const renderContent = () => {
    if (showRenameForm) {
      return (
        <SavedFilterForm
          onSave={onRename}
          initialValue={currentName}
          title={t('filters.views.rename')}
          onSuccess={handleClose}
          onCancel={() => setShowRenameForm(false)}
        />
      );
    }

    const menuItemBaseClass =
      'relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm leading-5 outline-none select-none [&_svg]:size-4';

    return (
      <div className="flex min-w-32 flex-col p-1">
        <button
          type="button"
          className={`${menuItemBaseClass} hover:bg-highlight-background [&_svg]:text-foreground-muted`}
          onClick={() => setShowRenameForm(true)}
        >
          <PencilIcon />
          {t('common.rename')}
        </button>
        <button
          type="button"
          className={`${menuItemBaseClass} text-destructive hover:bg-destructive/10`}
          onClick={() => {
            onDeleteClick();
            setOpen(false);
          }}
        >
          <Trash2Icon />
          {t('common.delete')}
        </button>
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button size="icon-sm" variant="ghost-neutral">
          <EllipsisVerticalIcon size={16} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0">
        {renderContent()}
      </PopoverContent>
    </Popover>
  );
};
